import { useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { redeemPairingCode } from '../api/settings'

/**
 * Email/password works inside the Desk iframe because it is a plain API call —
 * no popup, no redirect. Google needs a popup, which Zoho's iframe sandbox may
 * block.
 *
 * The pairing-code fallback stays hidden until a popup actually fails. Showing
 * it up front made the widget look like Google sign-in was expected to break,
 * when in most Desk setups the popup opens fine.
 */

// Codes meaning the popup could not open at all. `popup-closed-by-user` is
// deliberately absent: that is someone dismissing the window, and they can
// simply click again.
const POPUP_UNAVAILABLE = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
  'auth/internal-error',
])
/**
 * Firebase's messages are for developers — "Firebase: Error
 * (auth/user-not-found)." — and an agent typing a password should not have
 * to decode one. The codes that a person can act on get a sentence; the rest
 * fall back to something honest but readable.
 */
const AUTH_MESSAGES: Record<string, string> = {
  'auth/user-not-found': 'No account with that email. Create one, or check the address.',
  'auth/wrong-password': 'Wrong password. Try again, or use Forgot password.',
  'auth/invalid-credential': 'Wrong email or password.',
  'auth/invalid-login-credentials': 'Wrong email or password.',
  'auth/invalid-email': 'That does not look like an email address.',
  'auth/missing-password': 'Enter a password.',
  'auth/weak-password': 'Use a longer password, at least 6 characters.',
  'auth/email-already-in-use': 'An account with that email already exists. Sign in instead.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes, or reset your password.',
  'auth/network-request-failed': 'Could not reach the sign-in service. Check your connection and try again.',
  'auth/user-disabled': 'This account has been disabled. Contact support@orovim.com.',
  'auth/unauthorized-domain': 'Sign-in is not allowed from this address. Contact support@orovim.com.',
}

function describeAuthError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? ''
  if (AUTH_MESSAGES[code]) return AUTH_MESSAGES[code]
  const raw = e instanceof Error ? e.message : String(e)
  // Strip Firebase's wrapper if a code slipped through the table.
  return raw.replace(/^Firebase:\s*/i, '').replace(/^Error\s*\((auth\/[^)]+)\)\.?$/i, 'Sign-in failed ($1).')
}

export function SignInSection({ compact }: { compact?: boolean }) {
  const { signInEmail, signUpEmail, signInGoogle, signInWithCode, resetPassword } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<'in' | 'up'>('in')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [popupBlocked, setPopupBlocked] = useState(false)
  const [sentReset, setSentReset] = useState('')

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    try {
      await fn()
    } catch (e) {
      setError(describeAuthError(e))
    } finally {
      setBusy(false)
    }
  }

  async function googleSignIn() {
    setBusy(true)
    setError('')
    try {
      await signInGoogle()
    } catch (e) {
      const code = (e as { code?: string })?.code ?? ''
      if (POPUP_UNAVAILABLE.has(code)) {
        // Reveal the fallback rather than leaving a dead button behind.
        setPopupBlocked(true)
        setError('Desk blocked the Google popup. Use the pairing code below instead.')
      } else if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setError(describeAuthError(e))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>{mode === 'in' ? 'Sign in' : 'Create account'}</h2>

      {error && <div className="error">{error}</div>}

      <input
        type="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
      />

      <button
        disabled={busy || !email || !password}
        onClick={() =>
          run(() => (mode === 'in' ? signInEmail(email, password) : signUpEmail(email, password)))
        }
      >
        {busy ? 'Working…' : mode === 'in' ? 'Sign in' : 'Create account'}
      </button>

      <button
        className="secondary"
        onClick={() => {
          setMode(mode === 'in' ? 'up' : 'in')
          setError('')
        }}
      >
        {mode === 'in' ? 'Create an account instead' : 'I already have an account'}
      </button>

      {/* Only when signing in, and only once there is an address to send to —
          a reset link needs somewhere to go, and the field above is it. */}
      {mode === 'in' && (
        <button
          className="linkish"
          disabled={busy || !email}
          onClick={() =>
            run(async () => {
              await resetPassword(email)
              setSentReset(email)
            })
          }
        >
          Forgot password?
        </button>
      )}

      {sentReset && (
        <p className="note done">
          If {sentReset} has an account, a reset link is on its way.
        </p>
      )}

      <hr />

      <button className="secondary" disabled={busy} onClick={googleSignIn}>
        Continue with Google
      </button>

      {/* The code route used to appear only after a blocked popup. Google
          also refuses to sign in inside some embedded or managed browsers
          without blocking anything, which left no way in at all. */}
      {compact && !popupBlocked && (
        <button className="linkish" onClick={() => setPopupBlocked(true)}>
          Google sign-in not working here? Use a pairing code
        </button>
      )}

      {compact && popupBlocked && (
        <>
          <ol className="steps">
            <li>
              {/* The panel is inside an iframe, so this must open a real tab —
                  and the URL has to be shown, not described. */}
              <a href={`${window.location.origin}/settings`} target="_blank" rel="noreferrer">
                Open the settings page
              </a>{' '}
              in a new tab
            </li>
            <li>Sign in with Google there</li>
            <li>Under Profile, generate a pairing code</li>
            <li>Paste the code below</li>
          </ol>
          <input
            placeholder="Pairing code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={6}
            style={{ textTransform: 'uppercase', letterSpacing: '0.15em' }}
          />
          <button
            className="secondary"
            disabled={busy || code.length < 6}
            onClick={() =>
              run(async () => {
                const { token } = await redeemPairingCode(code)
                await signInWithCode(token)
              })
            }
          >
            Link this widget
          </button>
        </>
      )}
    </div>
  )
}
